/**
 * Main Cart Component
 * Handles quantity selector functionality with minus/plus buttons
 */

class QuantitySelector {
  constructor(container) {
    this.container = container;
    this.minusBtn = container.querySelector('.quantity-selector__btn--minus');
    this.plusBtn = container.querySelector('.quantity-selector__btn--plus');
    this.input = container.querySelector('.quantity-selector__input');
    this.min = parseInt(this.input.getAttribute('min')) || 1;
    this.max = parseInt(this.input.getAttribute('max')) || 999;
    
    // Initialize add to cart button quantity
    this.updateAddToCartButtonQuantity(this.input.value);
    
    this.init();
  }

  init() {
    this.bindEvents();
  }

  bindEvents() {
    // Minus button
    if (this.minusBtn) {
      this.minusBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this.decreaseQuantity();
      });
    }

    // Plus button
    if (this.plusBtn) {
      this.plusBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this.increaseQuantity();
      });
    }

    // Input change
    if (this.input) {
      this.input.addEventListener('change', () => {
        this.validateQuantity();
      });

      this.input.addEventListener('input', () => {
        this.validateQuantity();
      });
    }
  }

  decreaseQuantity() {
    const currentValue = parseInt(this.input.value) || this.min;
    const newValue = Math.max(this.min, currentValue - 1);
    this.updateQuantity(newValue);
  }

  increaseQuantity() {
    const currentValue = parseInt(this.input.value) || this.min;
    const newValue = Math.min(this.max, currentValue + 1);
    this.updateQuantity(newValue);
  }

  updateQuantity(value) {
    this.input.value = value;
    
    // Update any add to cart button in the same container
    this.updateAddToCartButtonQuantity(value);
    
    this.updateButtonStates();
    this.triggerQuantityChangeEvent(value);
  }

  updateAddToCartButtonQuantity(quantity) {
    const container = this.container.closest('.product-info__purchase') || this.container.closest('.product');
    const addToCartBtn = container?.querySelector('[data-add-to-cart]');
    if (addToCartBtn) {
      addToCartBtn.dataset.quantity = quantity;
    }
  }

  validateQuantity() {
    let value = parseInt(this.input.value) || this.min;
    
    if (value < this.min) {
      value = this.min;
    } else if (value > this.max) {
      value = this.max;
    }
    
    if (this.input.value != value) {
      this.input.value = value;
      // Update add to cart button
      this.updateAddToCartButtonQuantity(value);
    }
    
    this.updateButtonStates();
  }

  updateButtonStates() {
    const currentValue = parseInt(this.input.value) || this.min;
    
    // Update minus button
    if (this.minusBtn) {
      this.minusBtn.disabled = currentValue <= this.min;
    }
    
    // Update plus button
    if (this.plusBtn) {
      this.plusBtn.disabled = currentValue >= this.max;
    }
  }

  triggerQuantityChangeEvent(value) {
    const event = new CustomEvent('quantityChange', {
      detail: {
        quantity: value,
        timestamp: Date.now()
      }
    });
    
    this.container.dispatchEvent(event);
  }

  // Public methods
  getQuantity() {
    return parseInt(this.input.value) || this.min;
  }

  setQuantity(value) {
    const validValue = Math.max(this.min, Math.min(this.max, value));
    this.updateQuantity(validValue);
  }
}

// Legacy AddToCartForm removed - now using UniversalAddToCart for all add to cart functionality

// Cart Counter Manager
class CartCounter {
  constructor() {
    this.cartCountElement = document.querySelector('[data-cart-count]');
    this.cartIconElement = document.querySelector('[data-cart-icon]');
    this.currentCount = 0;
    
    this.init();
  }

  init() {
    this.loadCartCount();
    this.bindEvents();
  }

  bindEvents() {
    // Listen for cart item added events
    document.addEventListener('cartItemAdded', (event) => {
      this.updateCount(this.currentCount + event.detail.quantity);
    });

    // Listen for cart updates (if items are removed elsewhere)
    document.addEventListener('cartUpdated', () => {
      this.loadCartCount();
    });
  }

  async loadCartCount() {
    try {
      const response = await fetch('/cart.js');
      if (response.ok) {
        const cart = await response.json();
        this.updateCount(cart.item_count);
      }
    } catch (error) {
      console.error('Error loading cart count:', error);
    }
  }

  updateCount(count) {
    this.currentCount = count;
    
    if (this.cartCountElement) {
      if (count > 0) {
        this.cartCountElement.textContent = count;
        this.cartCountElement.style.display = 'flex';
      } else {
        this.cartCountElement.style.display = 'none';
      }
    }

    // Add animation class for visual feedback
    if (this.cartIconElement) {
      this.cartIconElement.classList.add('cart-updated');
      setTimeout(() => {
        this.cartIconElement.classList.remove('cart-updated');
      }, 300);
    }
  }
}

// Universal Add to Cart Handler - handles ALL add to cart buttons
class UniversalAddToCart {
  constructor() {
    this.init();
  }

  init() {
    this.bindEvents();
  }

  bindEvents() {
    // Listen for all add to cart buttons (both forms and standalone buttons)
    document.addEventListener('click', (e) => {
      const addToCartBtn = e.target.closest('[data-add-to-cart]');
      if (addToCartBtn) {
        e.preventDefault();
        e.stopPropagation(); // Prevent parent link navigation
        this.handleAddToCart(addToCartBtn);
      }
    });
  }

  async handleAddToCart(button) {
    if (button.disabled) return;

    // Show loading state
    this.setLoadingState(button, true);

    try {
      // Get data directly from button attributes - completely independent
      const variantId = button.dataset.variantId;
      const quantity = parseInt(button.dataset.quantity) || 1;

      if (!variantId) {
        console.error('No se pudo encontrar el variant ID en el botón');
        this.handleError({ message: 'Product variant not found' }, button);
        return;
      }

      // Check current cart quantity for this variant
      const currentCartQuantity = await this.getCurrentCartQuantity(variantId);
      const totalQuantity = currentCartQuantity + quantity;

      // Get variant stock information
      const variantStock = await this.getVariantStock(variantId);
      
      console.log('Variant stock info:', variantStock);
      console.log('Current cart quantity:', currentCartQuantity);
      console.log('Requested quantity:', quantity);
      console.log('Total quantity:', totalQuantity);
      
      if (variantStock && variantStock.inventory_quantity !== null) {
        console.log('Stock available:', variantStock.inventory_quantity);
        if (totalQuantity > variantStock.inventory_quantity) {
          const available = variantStock.inventory_quantity - currentCartQuantity;
          if (available <= 0) {
            this.handleError({ 
              message: `No hay stock disponible. Ya tienes ${currentCartQuantity} en tu carrito.` 
            }, button);
            return;
          } else {
            this.handleError({ 
              message: `Solo hay ${available} unidades disponibles. Ya tienes ${currentCartQuantity} en tu carrito.` 
            }, button);
            return;
          }
        }
      } else {
        console.log('No stock information found, proceeding without validation');
      }

      // Create FormData manually
      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', quantity.toString());

      // Check if button is in complementary/recommended products section - these should always be one-time purchases
      const isComplementaryProduct = button.closest('.superfood-picks-section') 
        || button.closest('.complementary-products')
        || button.closest('.product-recommendations')
        || button.closest('.recommended-products')
        || button.closest('[data-complementary-products]')
        || button.dataset.noSellingPlan === 'true';

      // Include Recharge/Shopify Subscriptions fields if present (widget v2)
      // Only for main product page, NOT for complementary/recommended products
      if (!isComplementaryProduct) {
        try {
          // Limit scope to PDP form/widget only. Do NOT read from whole document
          const contextEl = button.closest('form.product-form')
            || button.closest('.product-info__pricing')
            || button.closest('.product-detail');
          if (contextEl) {
          // selling_plan can be input or select depending on widget version
            const sellingPlanEl = contextEl.querySelector('input[name="selling_plan"], select[name="selling_plan"]');
            if (sellingPlanEl && sellingPlanEl.value) {
              formData.append('selling_plan', sellingPlanEl.value);
            }

            // Append any custom properties the widget may add
            const propertiesEls = contextEl.querySelectorAll('input[name^="properties["], select[name^="properties["]');
            propertiesEls.forEach((el) => {
              const name = el.getAttribute('name');
              if (!name) return;
              if (el instanceof HTMLInputElement && (el.type === 'checkbox' || el.type === 'radio')) {
                if (!el.checked) return;
                formData.append(name, el.value);
              } else {
                if (el.value !== undefined && el.value !== null && el.value !== '') {
                  formData.append(name, el.value);
                }
              }
            });
          }
        } catch (subsError) {
          console.warn('No subscription fields found or error reading them:', subsError);
        }
      }
      
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        body: formData
      });

      if (response.ok) {
        const result = await response.json();
        this.handleSuccess(result, button);
      } else {
        const error = await response.json();
        this.handleError(error, button);
      }
    } catch (error) {
      console.error('Error adding to cart:', error);
      this.handleError({ message: 'Error adding to cart' }, button);
    } finally {
      this.setLoadingState(button, false);
    }
  }

  async getCurrentCartQuantity(variantId) {
    try {
      const response = await fetch('/cart.js');
      if (!response.ok) return 0;
      
      const cart = await response.json();
      const item = cart.items.find(item => item.variant_id.toString() === variantId.toString());
      return item ? item.quantity : 0;
    } catch (error) {
      console.error('Error getting cart quantity:', error);
      return 0;
    }
  }

  async getVariantStock(variantId) {
    try {
      // First try to get from global product data
      const productData = window.productData || window.product;
      if (productData && productData.variants) {
        const variant = productData.variants.find(v => v.id.toString() === variantId.toString());
        if (variant) {
          return {
            inventory_quantity: variant.inventory_quantity,
            inventory_management: variant.inventory_management
          };
        }
      }

      // If not found, try to get from button data attributes
      const button = document.querySelector(`[data-variant-id="${variantId}"]`);
      if (button) {
        const inventoryQuantity = button.dataset.inventoryQuantity;
        const inventoryManagement = button.dataset.inventoryManagement;
        
        if (inventoryQuantity !== undefined) {
          return {
            inventory_quantity: parseInt(inventoryQuantity),
            inventory_management: inventoryManagement
          };
        }
      }

      // If still not found, try to get from product form
      const productForm = document.querySelector('form[action*="/cart/add"]');
      if (productForm) {
        const variantSelect = productForm.querySelector(`option[value="${variantId}"]`);
        if (variantSelect) {
          const inventoryQuantity = variantSelect.dataset.inventoryQuantity;
          const inventoryManagement = variantSelect.dataset.inventoryManagement;
          
          if (inventoryQuantity !== undefined) {
            return {
              inventory_quantity: parseInt(inventoryQuantity),
              inventory_management: inventoryManagement
            };
          }
        }
      }

      // Last resort: fetch product data from Shopify API
      try {
        const productHandle = this.getProductHandleFromUrl();
        if (productHandle) {
          const response = await fetch(`/products/${productHandle}.js`);
          if (response.ok) {
            const product = await response.json();
            const variant = product.variants.find(v => v.id.toString() === variantId.toString());
            if (variant) {
              return {
                inventory_quantity: variant.inventory_quantity,
                inventory_management: variant.inventory_management
              };
            }
          }
        }
      } catch (apiError) {
        console.log('Could not fetch product data from API:', apiError);
      }

      return null;
    } catch (error) {
      console.error('Error getting variant stock:', error);
      return null;
    }
  }

  getProductHandleFromUrl() {
    try {
      // Try to get product handle from current URL
      const path = window.location.pathname;
      const productMatch = path.match(/\/products\/([^\/]+)/);
      if (productMatch) {
        return productMatch[1];
      }

      // Try to get from product form action
      const productForm = document.querySelector('form[action*="/cart/add"]');
      if (productForm) {
        const action = productForm.getAttribute('action');
        const handleMatch = action.match(/\/products\/([^\/]+)/);
        if (handleMatch) {
          return handleMatch[1];
        }
      }

      return null;
    } catch (error) {
      console.error('Error getting product handle:', error);
      return null;
    }
  }

  createFormFromButton(button, productItem) {
    // Create a temporary form for standalone buttons
    const form = document.createElement('form');
    form.style.display = 'none';
    
    // Get product data from data attributes or DOM
    const variantId = button.dataset.variantId || 
                     productItem?.querySelector('[data-variant-id]')?.value ||
                     productItem?.querySelector('input[name="id"]')?.value;
    
    const quantity = button.dataset.quantity || 
                    productItem?.querySelector('[data-quantity-input]')?.value ||
                    productItem?.querySelector('input[name="quantity"]')?.value ||
                    '1';

    if (!variantId) {
      console.error('No se pudo encontrar el variant ID');
      return null;
    }

    // Add hidden inputs
    const variantInput = document.createElement('input');
    variantInput.type = 'hidden';
    variantInput.name = 'id';
    variantInput.value = variantId;
    form.appendChild(variantInput);

    const quantityInput = document.createElement('input');
    quantityInput.type = 'hidden';
    quantityInput.name = 'quantity';
    quantityInput.value = quantity;
    form.appendChild(quantityInput);

    document.body.appendChild(form);
    return form;
  }

  setLoadingState(button, loading) {
    if (loading) {
      button.disabled = true;
      button.classList.add('adding-to-cart');
      
      // Handle different button structures
      const addToCartText = button.querySelector('.add-to-cart-text');
      const addingToCartText = button.querySelector('.adding-to-cart-text');
      
      if (addToCartText && addingToCartText) {
        // Structure with separate spans (main product)
        addToCartText.style.display = 'none';
        addingToCartText.style.display = 'inline';
      } else {
        // Simple button structure (related products, etc.)
        button.dataset.originalText = button.textContent;
        button.textContent = 'Adding...';
      }
    } else {
      button.disabled = false;
      button.classList.remove('adding-to-cart');
      
      // Handle different button structures
      const addToCartText = button.querySelector('.add-to-cart-text');
      const addingToCartText = button.querySelector('.adding-to-cart-text');
      
      if (addToCartText && addingToCartText) {
        // Structure with separate spans (main product)
        addToCartText.style.display = 'inline';
        addingToCartText.style.display = 'none';
      } else {
        // Simple button structure (related products, etc.)
        button.textContent = button.dataset.originalText || 'Adding';
      }
    }
  }

  handleSuccess(result, button) {
    // Trigger custom event for other components
    const event = new CustomEvent('cartItemAdded', {
      detail: {
        item: result,
        quantity: parseInt(button.dataset.quantity || '1')
      }
    });
    
    document.dispatchEvent(event);

    // Show success feedback
    this.showSuccessFeedback(button);
  }

  handleError(error, button) {
    console.error('Add to cart error:', error);
    this.showErrorFeedback(button, error.message || 'Error adding to cart');
  }

  showSuccessFeedback(button) {
    // Get configuration from global config or use defaults
    const config = window.addToCartConfig || {};
    const successText = config.successText || 'Added!';
    const successColor = config.successColor || '#ffffff';
    
    const addToCartText = button.querySelector('.add-to-cart-text');
    const addingToCartText = button.querySelector('.adding-to-cart-text');
    
    if (addToCartText && addingToCartText) {
      // Structure with separate spans (main product) - just show success briefly
      const originalText = addToCartText.textContent;
      addToCartText.textContent = successText;
      addToCartText.style.color = successColor;
      
      setTimeout(() => {
        addToCartText.textContent = originalText;
        addToCartText.style.color = '';
      }, 2000);
    } else {
      // Simple button structure (PLP cards, related products, etc.)
      const originalText = button.dataset.originalText || button.textContent;
      const originalBg = button.style.backgroundColor || getComputedStyle(button).backgroundColor;
      const originalColor = button.style.color || getComputedStyle(button).color;
      
      // Store originals if not already stored
      if (!button.dataset.originalBg) {
        button.dataset.originalBg = originalBg;
      }
      if (!button.dataset.originalColor) {
        button.dataset.originalColor = originalColor;
      }
      
      // Apply success styling - make it very visible
      button.textContent = successText;
      button.style.color = '#ffffff'; // White text
      button.classList.add('add-to-cart-success');
      
      setTimeout(() => {
        button.textContent = originalText;
        button.style.backgroundColor = button.dataset.originalBg || originalBg;
        button.style.color = button.dataset.originalColor || originalColor;
        button.classList.remove('add-to-cart-success');
      }, 2000);
    }
  }

  showErrorFeedback(button, message) {
    const addToCartText = button.querySelector('.add-to-cart-text');
    const addingToCartText = button.querySelector('.adding-to-cart-text');
    
    if (addToCartText && addingToCartText) {
      // Structure with separate spans (main product)
      const originalText = addToCartText.textContent;
      addToCartText.textContent = 'Error';
      addToCartText.style.color = '#ff6b6b';
      
      // Show error message
      this.showMessage(message, 'error', button);
      
      setTimeout(() => {
        addToCartText.textContent = originalText;
        addToCartText.style.color = '';
      }, 2000);
    } else {
      // Simple button structure (related products, etc.)
      const originalText = button.dataset.originalText || button.textContent;
      button.textContent = 'Error';
      button.style.backgroundColor = '#ff6b6b';
      button.style.color = 'white';
      
      // Show error message
      this.showMessage(message, 'error', button);
      
      setTimeout(() => {
        button.textContent = originalText;
        button.style.backgroundColor = '';
        button.style.color = '';
      }, 2000);
    }
  }

  showMessage(message, type, button) {
    // Create or update message element
    let messageEl = document.querySelector('.cart-message');
    if (!messageEl) {
      messageEl = document.createElement('div');
      messageEl.className = 'cart-message';
      document.body.appendChild(messageEl);
    }

    messageEl.textContent = message;
    messageEl.className = `cart-message cart-message--${type}`;
    
    // Position near the button
    const rect = button.getBoundingClientRect();
    messageEl.style.position = 'fixed';
    messageEl.style.top = `${rect.bottom + 10}px`;
    messageEl.style.left = `${rect.left}px`;
    messageEl.style.zIndex = '9999';
    
    // Auto-hide after 3 seconds
    setTimeout(() => {
      if (messageEl) {
        messageEl.remove();
      }
    }, 3000);
  }
}

// Initialize all components on the page
document.addEventListener('DOMContentLoaded', () => {
  // Initialize quantity selectors
  const quantitySelectors = document.querySelectorAll('.quantity-selector');
  quantitySelectors.forEach(container => {
    new QuantitySelector(container);
  });

  // Initialize universal add to cart handler (handles all add to cart buttons)
  new UniversalAddToCart();

  // Initialize cart counter
  new CartCounter();
});

// Export for module usage
if (typeof module !== 'undefined' && module.exports) {
  module.exports = { QuantitySelector, UniversalAddToCart, CartCounter };
}
